/**
 * Shared field-apply for the patient create / edit voice flows.
 *
 * Maps a spoken (field, value) onto a react-hook-form field, reusing the
 * same name-accumulation / date-parse / gender-match logic for both
 * Create Patient and Edit Patient Details. `nameRef` holds the running
 * { first, middle, last } so first/middle/last updates compose into the
 * single `patientName` field. For EDIT, seed nameRef from the existing
 * name first so partial updates preserve the other name parts.
 *
 * Throws invalid_* errors the agent can relay; returns a small result.
 */

import dayjs from "dayjs";

import { formatToE164 } from "config/helpers/common_helper";

const GENDER_OPTIONS = [
  { match: ["male", "m", "man", "boy"], value: "male" },
  { match: ["female", "f", "woman", "girl"], value: "female" },
  { match: ["other", "non-binary", "nonbinary", "non binary"], value: "other" },
  { match: ["unknown", "prefer not to say", "not specified", "unspecified"], value: "unknown" },
];

export function applyPatientField({ field, value, setValue, clearErrors, nameRef }) {
  const f = String(field ?? "").trim().toLowerCase();
  const v = String(value ?? "").trim();
  if (!v) throw new Error("invalid_value: no value was given.");

  const setField = (key, val) => {
    setValue(key, val);
    clearErrors?.(key);
  };

  switch (f) {
    case "first_name":
    case "middle_name":
    case "last_name": {
      const part = f.split("_")[0]; // first | middle | last
      nameRef.current[part] = v;
      const full = [nameRef.current.first, nameRef.current.middle, nameRef.current.last]
        .filter(Boolean)
        .join(" ");
      setField("patientName", full);
      return { field: f, value: v, name: full, changed: true };
    }
    case "date_of_birth": {
      let d = dayjs(v);
      if (!d.isValid()) d = dayjs(new Date(v));
      if (!d.isValid()) {
        throw new Error(
          `invalid_date: '${value}' isn't a date I can read. Ask the user to repeat it, e.g. 'January 5th 1990'.`,
        );
      }
      setField("dob", d);
      return { field: f, value: d.format("YYYY-MM-DD"), changed: true };
    }
    case "age": {
      // Age -> APPROXIMATE date of birth: today minus N years. The current age
      // comes out exact, but the specific day/month is a placeholder — flagged
      // `approximate` so the copilot tells the clinician to correct it if they
      // have the exact date. An explicit date_of_birth always wins over this
      // (the caller drops age when a real DOB is also given).
      const n = parseInt(String(v).replace(/[^\d]/g, ""), 10);
      if (!Number.isFinite(n) || n <= 0 || n > 150) {
        throw new Error(
          `invalid_age: '${value}' isn't an age I can use. Ask the user for the age in years, e.g. '28'.`,
        );
      }
      const d = dayjs().subtract(n, "year");
      setField("dob", d);
      return {
        field: "date_of_birth",
        value: d.format("YYYY-MM-DD"),
        approximate: true,
        changed: true,
      };
    }
    case "gender": {
      const g = GENDER_OPTIONS.find((o) => o.match.includes(v.toLowerCase()));
      if (!g) {
        throw new Error("invalid_option: gender must be male, female, other, or unknown.");
      }
      setField("gender", g.value);
      return { field: f, value: g.value, changed: true };
    }
    case "phone": {
      // Normalise to E.164 using the SAME validator the form applies at save
      // time (formatToE164). Spoken numbers rarely include the leading "+",
      // so add it over the raw digits — this lets a spoken country code parse
      // but never guesses a country for the user. If it still can't validate,
      // throw invalid_phone so it surfaces under `invalid[]` and the model
      // asks the user to repeat with a country code, instead of the number
      // being silently dropped from the payload at save time.
      const candidate = v.startsWith("+") ? v : `+${v.replace(/[^\d]/g, "")}`;
      const e164 = formatToE164(candidate);
      if (!e164) {
        throw new Error(
          `invalid_phone: '${value}' isn't a valid phone number. Ask the user to repeat it including the country code, e.g. 'plus one, five five five...'.`,
        );
      }
      setField("patientPhone", e164);
      return { field: f, value: e164, changed: true };
    }
    case "email":
      setField("patientEmail", v);
      return { field: f, value: v, changed: true };
    default:
      throw new Error(`invalid_field: '${field}' isn't an editable patient field.`);
  }
}
